import achievementJson from "../content/achievements.json";
import type { GameState, SaveData } from "./types";

export type AchievementDefinition = { id: string; name: string; description: string };
export const achievements = achievementJson as AchievementDefinition[];

/**
 * Snapshot passed to checkAchievements once a run has finished and `save` has
 * already been mutated with that run's rewards/progression (see
 * RunSession.persistResult). `state` is the final GameState of the run that
 * just ended, so per-run facts (best combo, traps triggered, secret rooms,
 * curses) can be read directly off it.
 */
export type AchievementEvent = {
  save: SaveData;
  state: GameState;
  won: boolean;
};

type Condition = (event: AchievementEvent) => boolean;

const conditions: Record<string, Condition> = {
  "first-expedition": ({ won, save }) => won && save.progression.runs >= 1,
  "ruins-apprentice": ({ save }) => save.progression.completedLevels.length >= 5,
  "ruins-veteran": ({ save }) => save.progression.completedLevels.length >= 10,
  "relic-hunter": ({ save }) => save.unlockedRelics.length >= 1,
  "relic-master": ({ save }) => save.unlockedRelics.length >= 5,
  "score-seeker": ({ save }) => save.progression.bestScore >= 1000,
  "score-legend": ({ save }) => save.progression.bestScore >= 5000,
  "combo-chain": ({ state }) => state.combo.best >= 5,
  "treasure-hoarder": ({ save }) => save.progression.totalTreasuresCollected >= 100,
  "flawless-vault": ({ won, state }) => won && state.trapsTriggered === 0,
  "secret-seeker": ({ state }) => Boolean(state.lastSecretRoom),
  "curse-defiant": ({ won, state }) => won && Boolean(state.lastCurseMessage),
  "daily-first-steps": ({ save }) => save.daily.completedDates.length >= 1,
  "daily-devotion": ({ save }) => save.daily.streak >= 7,
  "gold-baron": ({ save }) => save.economy.gold >= 5000,
};

/** Returns the achievement definitions that newly unlocked given this event. */
export function checkAchievements(event: AchievementEvent): AchievementDefinition[] {
  return achievements.filter(achievement =>
    !event.save.achievements.includes(achievement.id) &&
    (conditions[achievement.id]?.(event) ?? false));
}
